import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { useMount } from "react-use";
import { Filter } from "@activecollab/components";
import { useGlobalPrivateChannelListener } from "~/components/Hooks/useGlobalPrivateChannelListener";
import { urlParamsToSelection } from "~/pages/ProjectTasksArchive/urlParamsToSelection";
import { useTasksArchiveAvailableFilters } from "~/repository/useTasks";
import { fetchAllTaskLists } from "~/store/actions/actions.taskLists";
import { getAllTaskLabels } from "~/store/selectors/selector.labels";
import { getAllTaskListsByProjectId } from "~/store/selectors/selector.taskLists";
import { getAllUsersMap } from "~/store/selectors/selector.users";

const TasksArchiveFilter = () => {
  const { t } = useTranslation();
  const { projectId: _projectId } = useParams();
  const projectId = useMemo(() => Number(_projectId), [_projectId]);
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const { data: rawAvailableFilters, refetch: refetchRawAvailableFilters } = useTasksArchiveAvailableFilters(projectId);

  const usersIdNameMap = useSelector(getAllUsersMap);
  const labelsIdNameMap = useSelector(getAllTaskLabels).toMap().toJS();
  const taskListsIdNameMap = useSelector((state) => getAllTaskListsByProjectId(state, projectId))
    .toMap()
    .toJS();

  const filterSelected = useMemo(() => {
    return urlParamsToSelection(searchParams);
  }, [searchParams]);

  const filterParams = useMemo(() => {
    const taskListSubmenu = [];
    const assigneeSubmenu = [];
    const labelSubmenu = [];
    const completedBySubmenu = [];

    const completedOnRangeOptions = [
      { id: "this_week", name: t("This Week") },
      { id: "last_week", name: t("Last Week") },
      { id: "this_month", name: t("This Month") },
      { id: "last_month", name: t("Last Month") },
      { id: "this_quarter", name: t("This Quarter") },
      { id: "last_quarter", name: t("Last Quarter") },
      { id: "this_year", name: t("This Year") },
      { id: "last_year", name: t("Last Year") },
    ];

    const dueDateOnRangeOptions = [{ id: "not_set", name: t("Not Set") }, ...completedOnRangeOptions];

    if (rawAvailableFilters?.task_list?.length) {
      rawAvailableFilters.task_list.forEach((filterTaskListId) => {
        taskListSubmenu.push({
          id: filterTaskListId,
          name: taskListsIdNameMap[filterTaskListId]?.name || t("Unknown Task List"),
        });
      });
    }

    if (rawAvailableFilters?.assignee?.length) {
      rawAvailableFilters.assignee.forEach((filterAssigneeId) => {
        assigneeSubmenu.push({
          id: filterAssigneeId,
          name: usersIdNameMap[filterAssigneeId]?.display_name || t("Unknown User"),
        });
      });
    }

    if (rawAvailableFilters?.completed_by?.length) {
      rawAvailableFilters.completed_by.forEach((filterCompletedById) => {
        completedBySubmenu.push({
          id: filterCompletedById,
          name: usersIdNameMap[filterCompletedById]?.display_name || t("Unknown User"),
        });
      });
    }

    if (rawAvailableFilters?.label?.length) {
      rawAvailableFilters.label.forEach((filterLabelId) => {
        labelSubmenu.push({
          id: filterLabelId,
          name: labelsIdNameMap[filterLabelId]?.name || t("Unknown User"),
        });
      });
    }

    assigneeSubmenu.sort((first, second) => Number(first.name > second.name ? 1 : -1));
    assigneeSubmenu.unshift({ id: 0, name: t("Unassigned") });

    labelSubmenu.sort((first, second) => Number(first.name > second.name ? 1 : -1));
    labelSubmenu.unshift({ id: 0, name: t("Not Set") });

    return [
      {
        id: "taskListFilter",
        title: t("Task List"),
        submenu: taskListSubmenu,
        searchPlaceholder: t("Search by task list") + "...",
      },
      {
        id: "assigneeFilter",
        title: t("Assignee"),
        submenu: assigneeSubmenu,
        disableInternalSort: true,
        searchPlaceholder: t("Search by assignee") + "...",
      },
      {
        id: "dueDateFilter",
        title: t("Due Date"),
        submenu: dueDateOnRangeOptions,
        isSingleSelect: true,
        disableInternalSort: true,
        disableSearch: true,
      },
      {
        id: "labelFilter",
        title: t("Label"),
        submenu: labelSubmenu,
        disableInternalSort: true,
        searchPlaceholder: t("Search by label") + "...",
      },
      {
        id: "completedByFilter",
        title: t("Completed By"),
        submenu: completedBySubmenu,
        disableInternalSort: true,
        disableSearch: true,
      },
      {
        id: "completionDateFilter",
        title: t("Completion Date"),
        submenu: completedOnRangeOptions,
        isSingleSelect: true,
        disableInternalSort: true,
        disableSearch: true,
      },
    ];
  }, [t, taskListsIdNameMap, usersIdNameMap, labelsIdNameMap, rawAvailableFilters]);

  const handleOnFilterChange = useCallback(
    (value) => {
      const options = [
        { id: "taskListFilter", key: "task_list" },
        { id: "assigneeFilter", key: "assignee" },
        { id: "dueDateFilter", key: "due_date" },
        { id: "labelFilter", key: "label" },
        { id: "completionDateFilter", key: "completion_date" },
        { id: "completedByFilter", key: "completed_by" },
      ];

      options.forEach((option) => {
        value[option.id]?.length > 0 ? searchParams.set(option.key, value[option.id]) : searchParams.delete(option.key);
      });

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  useMount(() => {
    dispatch(fetchAllTaskLists(projectId));
  });

  const realtimeListener = useCallback(
    (updatedObject) => {
      if (!updatedObject) return false;

      if (updatedObject?.project_id === projectId) {
        refetchRawAvailableFilters();
      }
    },
    [projectId, refetchRawAvailableFilters]
  );

  useGlobalPrivateChannelListener("TaskUpdated", realtimeListener);
  useGlobalPrivateChannelListener("TaskMovedToTrash", realtimeListener);
  useGlobalPrivateChannelListener("TaskRestoredFromTrash", realtimeListener);
  useGlobalPrivateChannelListener("TaskListUpdated", realtimeListener);
  useGlobalPrivateChannelListener("TaskListMovedToTrash", realtimeListener);
  useGlobalPrivateChannelListener("TaskListRestoredFromTrash", realtimeListener);

  return (
    <Filter
      data={filterParams}
      onChange={handleOnFilterChange}
      selected={filterSelected}
      label={t("Filter")}
      clearAllText={t("Clear All")}
      resetLabel={t("Reset")}
      noResultText={t("No results")}
    />
  );
};

TasksArchiveFilter.displayName = "TasksArchiveFilter";
TasksArchiveFilter.propTypes = {};

export default TasksArchiveFilter;
